import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, catchError, map, of, throwError } from 'rxjs';

import type {
  ApiRequest,
  McResponse,
} from '@mimecast-ui/shared/data-access-core/api-types';
import { ConfigService } from '@mimecast-ui/universal-login/data-access-config';

import { LoginSessionService } from './login-session.service';
import {
  AuthenticationType,
  ChangePasswordError,
  ChangePasswordRequest,
  ChangePasswordResponseData,
  DiscoverAuthenticationData,
  DiscoverAuthenticationInput,
  DiscoverAuthenticationRequest,
  DiscoverAuthenticationResponse,
  EnrollSmsRequest,
  EnrollSmsResponse,
  GetOidcResponseRequest,
  GetPasswordRulesPayload,
  GetPasswordRulesRequest,
  GetSamlResponseRequest,
  LAST_USER_TOKEN_COOKIE,
  LOGIN_ERROR_CODES,
  LoginError,
  LoginRequest,
  LoginResponseData,
  MfaError,
  OidcIdpRedirectRequest,
  OtpRegistrationData,
  PASSWORD_RESET_ERROR_CODES,
  PasswordResetError,
  PasswordRules,
  PasswordRulesConfig,
  RegisterOtpRequest,
  ResetPasswordPayload,
  ResetPasswordRequestPayload,
  ResetPasswordRequestResponse,
  SamlIdpRedirectRequest,
  SecureMessagingResetPasswordPayload,
  SendConfirmationPayload,
  SendConfirmationResponse,
  SendNotificationPayload,
  SendNotificationResponse,
  SetSecureMessagingPasswordPayload,
  SsoAuthTypeKey,
  SsoError,
  SsoLoginRequest,
  SsoParams,
  SsoSamlRedirectRequest,
  SsoSamlRedirectResponse,
  SsoSubmitVerificationCodeRequest,
  SubmitVerificationCodeRequest,
  SubmitVerificationCodeResponse,
  ValidAuthTypeKey,
  ValidateCaptchaPayload,
  ValidateCaptchaResponse,
} from './types';

const DISCOVER_AUTHENTICATION_URL =
  '/u/proxy/api/login/discover-authentication';
const LOGIN_URL = '/u/rest/api/login/login';
const FEDERATION_IDP_LOGIN_URL = '/u/proxy/api/login/login';
const GET_SAML_RESPONSE_URL = '/u/rest/api/login/get-saml-response';
const GET_OIDC_RESPONSE_URL = '/u/rest/api/login/get-oidc-response';
const SUBMIT_VERIFICATION_CODE_URL =
  '/u/rest/api/login/submit-verification-code';
const SSO_LOGIN_URL = '/u/rest/api/login/sso/login';
const SSO_SUBMIT_VERIFICATION_CODE_URL =
  '/u/rest/api/login/sso/submit-verification-code';
const SSO_SAML_REDIRECT_URL = '/u/rest/api/login/sso/saml-redirect';
const LOGOUT_URL = '/u/rest/api/login/logout';
const RESET_PASSWORD_REQUEST_URL = '/u/proxy/api/login/reset-password/request';
const RESET_PASSWORD_VALIDATE_CAPTCHA_URL =
  '/u/proxy/api/login/reset-password/validate-captcha-response';
const RESET_PASSWORD_SEND_NOTIFICATION_URL =
  '/u/proxy/api/login/reset-password/send-notification';
const RESET_PASSWORD_SEND_CONFIRMATION_URL =
  '/u/proxy/api/login/reset-password/send-confirmation';
const RESET_PASSWORD_RESET_URL = '/u/proxy/api/login/reset-password/reset';
const SM_RESET_PASSWORD_URL = '/u/proxy/api/securemessaging/reset-password';
const GET_PASSWORD_RULES_URL = '/u/proxy/api/login/get-password-rules';
const CHANGE_PASSWORD_URL = '/u/proxy/api/login/change-password';
const SM_SET_PASSWORD_URL = '/u/proxy/api/securemessaging/set-password';

const AUTH_TYPE_KEY_BY_UPPERCASE_VALUE: Record<string, ValidAuthTypeKey> = {
  'BASIC-CLOUD': 'Basic-Cloud',
  'BASIC-AD': 'Basic-AD',
  'BASIC-EXTERNAL': 'Basic-External',
};
const REGISTER_OTP_URL = '/u/proxy/api/login/register-otp';
const ENROLL_SMS_URL = '/u/proxy/api/login/enroll-sms';

interface LoginFailureBody {
  fail?: { errors?: { code?: string }[]; key?: Record<string, unknown> }[];
  data?: { changePasswordToken?: string }[];
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly cookieService = inject(CookieService);
  private readonly loginSessionService = inject(LoginSessionService);

  discoverAuthentication(
    input: DiscoverAuthenticationInput
  ): Observable<DiscoverAuthenticationResponse> {
    const payload: DiscoverAuthenticationRequest =
      'emailAddress' in input
        ? { emailAddress: input.emailAddress, ...this.resolveCustomerToken() }
        : input;

    return this.http
      .post<
        McResponse<DiscoverAuthenticationData>
      >(DISCOVER_AUTHENTICATION_URL, payload)
      .pipe(map((response) => this.mapDiscoverAuthResponse(response)));
  }

  login(
    username: string,
    password: string,
    authenticationType: ValidAuthTypeKey
  ): Observable<LoginResponseData> {
    const payload: ApiRequest<LoginRequest> = {
      data: [
        {
          username,
          password,
          authenticationType,
          ...this.resolveCustomerToken(),
          extendOnValidate: true,
          tokenType: 'key',
        },
      ],
    };

    return this.http
      .post<McResponse<LoginResponseData>>(LOGIN_URL, payload, {
        headers: this.buildLoginHeaders(),
      })
      .pipe(
        map((response) => this.mapLoginResponse(response)),
        catchError((error: unknown) =>
          throwError(() => {
            if (error instanceof LoginError) {
              return error;
            }

            const body =
              ((error as HttpErrorResponse).error as LoginFailureBody | null) ??
              {};
            return this.buildLoginError(body);
          })
        )
      );
  }

  /**
   * Completes the MFA challenge created by `login()`. The BFF looks up the
   * temporary session from the `sid` header (legacy interceptor), not a cookie.
   */
  submitVerificationCode(
    code: string,
    sessionId?: string | null
  ): Observable<SubmitVerificationCodeResponse> {
    const payload: ApiRequest<SubmitVerificationCodeRequest> = {
      data: [{ code: code.replace(/\s/g, '') }],
    };

    return this.http
      .post<
        McResponse<SubmitVerificationCodeResponse>
      >(SUBMIT_VERIFICATION_CODE_URL, payload, { headers: this.buildLoginHeaders(sessionId) })
      .pipe(
        map((response) => this.mapVerificationCodeResponse(response)),
        catchError((error: unknown) =>
          throwError(() => {
            if (error instanceof MfaError) {
              return error;
            }

            return this.buildMfaError(this.resolveFailureBody(error));
          })
        )
      );
  }

  /**
   * Password login for Mimecast-as-IdP SSO broker (`gta=sso`). Uses the SSO
   * login endpoint and maps the email argument to `emailAddress`.
   */
  loginSso(
    username: string,
    password: string,
    authenticationType: SsoAuthTypeKey,
    ssoParams: SsoParams,
    samlResultPostTarget?: string
  ): Observable<LoginResponseData> {
    const payload: ApiRequest<SsoLoginRequest> = {
      data: [
        {
          emailAddress: username,
          token: ssoParams.token,
          password,
          authenticationType,
          ...(ssoParams.relaystate ? { relayState: ssoParams.relaystate } : {}),
          ...(samlResultPostTarget ? { samlResultPostTarget } : {}),
          ...(ssoParams.context ? { context: ssoParams.context } : {}),
        },
      ],
    };
    return this.http
      .post<McResponse<LoginResponseData>>(SSO_LOGIN_URL, payload, {
        headers: this.buildLoginHeaders(),
      })
      .pipe(
        map((response) => this.mapLoginResponse(response)),
        catchError((error: unknown) =>
          throwError(() => {
            if (error instanceof LoginError) {
              return error;
            }
            const body =
              ((error as HttpErrorResponse).error as LoginFailureBody | null) ??
              {};
            return this.buildLoginError(body);
          })
        )
      );
  }

  /**
   * Completes MFA for Mimecast-as-IdP SSO broker. Same `sid` session model as
   * `submitVerificationCode`, plus legacy `token` / `context` / `relayState`.
   */
  submitVerificationCodeSso(
    code: string,
    ssoParams: SsoParams,
    sessionId?: string | null
  ): Observable<SubmitVerificationCodeResponse> {
    const payload: ApiRequest<SsoSubmitVerificationCodeRequest> = {
      data: [
        {
          code: code.replace(/\s/g, ''),
          token: ssoParams.token,
          relayState: ssoParams.relaystate,
          context: ssoParams.context,
        },
      ],
    };
    return this.http
      .post<McResponse<SubmitVerificationCodeResponse>>(
        SSO_SUBMIT_VERIFICATION_CODE_URL,
        payload,
        {
          headers: this.buildLoginHeaders(sessionId),
        }
      )
      .pipe(
        map((response) => this.mapVerificationCodeResponse(response)),
        catchError((error: unknown) =>
          throwError(() => {
            if (error instanceof MfaError) {
              return error;
            }
            return this.buildMfaError(this.resolveFailureBody(error));
          })
        )
      );
  }

  /**
   * Fetches SAML assertion payload for Mimecast-as-IdP broker handoff to the
   * external SP (POST SAMLResponse + RelayState).
   */
  getSAMLRedirectSso(
    ssoParams: SsoParams,
    emailAddress: string,
    sessionId?: string | null
  ): Observable<SsoSamlRedirectResponse> {
    const payload: ApiRequest<SsoSamlRedirectRequest> = {
      data: [
        {
          emailAddress,
          token: ssoParams.token,
          ...(ssoParams.relaystate ? { relayState: ssoParams.relaystate } : {}),
          ...(ssoParams.context ? { context: ssoParams.context } : {}),
        },
      ],
    };
    return this.http
      .post<McResponse<SsoSamlRedirectResponse>>(
        SSO_SAML_REDIRECT_URL,
        payload,
        {
          headers: this.buildLoginHeaders(sessionId),
        }
      )
      .pipe(
        map((response) => {
          if (response.fail?.length) {
            throw new SsoError(
              this.getFailCode(response as unknown as LoginFailureBody)
            );
          }
          const data = response.data?.[0] as
            | SsoSamlRedirectResponse
            | undefined;
          if (!data?.redirectUrl || !data?.samlResponse) {
            throw new SsoError('sso_redirect_malformed');
          }
          return data;
        }),
        catchError((error: unknown) =>
          throwError(() => {
            if (error instanceof SsoError) {
              return error;
            }
            const body =
              ((error as HttpErrorResponse).error as LoginFailureBody | null) ??
              {};
            return new SsoError(this.getFailCode(body));
          })
        )
      );
  }

  /**
   * Invalidates the Mimecast login session. Body is a plain object (not the
   * `{ data: [...] }` envelope) to match legacy MUL and AuthenticationResource.
   * `skipRedirect: true` (WF-2117) keeps IdP-backed logouts on a 200 body so
   * callers such as the SSO broker are not navigated away.
   */
  logout(): Observable<unknown> {
    return this.http.post(
      LOGOUT_URL,
      { skipRedirect: true },
      {
        headers: this.buildLoginHeaders(),
      }
    );
  }

  /**
   * Starts customer-IdP SAML by posting to the proxy login endpoint with
   * `Authorization: SAML`. The expected outcome is HTTP 401 carrying
   * `err_xdk_authenticate_with_identity_provider` and an `idpUrl` to navigate to.
   */
  initiateSamlIdpRedirect(
    username: string,
    samlResultPostTarget: string
  ): Observable<string> {
    const { xMcAppId } = this.configService.resolveAppConfig();
    const payload: SamlIdpRedirectRequest = {
      username,
      ...this.resolveCustomerToken(),
      ...(xMcAppId ? { appId: xMcAppId } : {}),
      extendOnValidate: true,
      tokenType: 'key',
      samlResultPostTarget,
    };

    return this.postFederationIdpRedirect(payload, 'SAML');
  }

  /**
   * Starts Microsoft OIDC by posting to the proxy login endpoint with
   * `Authorization: OIDC`. The expected outcome is HTTP 401 carrying
   * `err_xdk_authenticate_with_identity_provider` and an `idpUrl` to navigate to.
   */
  initiateOidcIdpRedirect(
    username: string,
    oidcResultPostTarget: string
  ): Observable<string> {
    const { xMcAppId } = this.configService.resolveAppConfig();
    const payload: OidcIdpRedirectRequest = {
      username,
      ...this.resolveCustomerToken(),
      ...(xMcAppId ? { appId: xMcAppId } : {}),
      extendOnValidate: true,
      tokenType: 'key',
      oidcResultPostTarget,
    };

    return this.postFederationIdpRedirect(payload, 'OIDC');
  }

  /**
   * Completes a SAML return (`?status=true&key=...`). Does not write the `lu`
   * cookie - matching legacy SAML success handling. Writes `mc-sg` via
   * {@link LoginSessionService} before the response is emitted.
   */
  getSamlResponse(key: string): Observable<LoginResponseData> {
    return this.getFederationResponse(GET_SAML_RESPONSE_URL, key);
  }

  /**
   * Completes an OIDC return (`?status=true&key=...`). Does not write the `lu`
   * cookie - matching legacy OIDC success handling. Writes `mc-sg` via
   * {@link LoginSessionService} before the response is emitted.
   */
  getOidcResponse(key: string): Observable<LoginResponseData> {
    return this.getFederationResponse(GET_OIDC_RESPONSE_URL, key);
  }

  private postFederationIdpRedirect(
    payload: SamlIdpRedirectRequest | OidcIdpRedirectRequest,
    authorization: 'SAML' | 'OIDC'
  ): Observable<string> {
    return this.http
      .post<McResponse<unknown>>(FEDERATION_IDP_LOGIN_URL, payload, {
        headers: new HttpHeaders({ Authorization: authorization }),
      })
      .pipe(
        map(() => {
          throw new LoginError('');
        }),
        catchError((error: unknown) => {
          if (error instanceof LoginError) {
            return throwError(() => error);
          }

          const httpError = error as HttpErrorResponse;
          const body = (httpError.error as LoginFailureBody | null) ?? {};
          const code = this.getFailCode(body);

          if (code === LOGIN_ERROR_CODES.AUTHENTICATE_WITH_IDENTITY_PROVIDER) {
            const idpUrl = body.fail?.[0]?.key?.['idpUrl'];
            if (typeof idpUrl === 'string' && idpUrl.trim()) {
              return of(idpUrl.trim());
            }
          }

          return throwError(() => this.buildLoginError(body));
        })
      );
  }

  private getFederationResponse(
    url: string,
    key: string
  ): Observable<LoginResponseData> {
    const payload: ApiRequest<GetSamlResponseRequest | GetOidcResponseRequest> =
      {
        data: [{ key }],
      };

    return this.http
      .post<McResponse<LoginResponseData>>(url, payload, {
        headers: this.buildLoginHeaders(),
      })
      .pipe(
        map((response) => this.mapFederationResponse(response)),
        catchError((error: unknown) =>
          throwError(() => {
            if (error instanceof LoginError) {
              return error;
            }

            const body =
              ((error as HttpErrorResponse).error as LoginFailureBody | null) ??
              {};
            return this.buildLoginError(body);
          })
        )
      );
  }

  requestPasswordReset(
    payload: ResetPasswordRequestPayload
  ): Observable<ResetPasswordRequestResponse> {
    return this.http
      .post<
        McResponse<ResetPasswordRequestResponse>
      >(RESET_PASSWORD_REQUEST_URL, payload)
      .pipe(
        map((response) =>
          this.mapPasswordResetResponse(response, (data) => {
            if (!data?.captcha || !data?.token) {
              throw new PasswordResetError(PASSWORD_RESET_ERROR_CODES.GENERIC);
            }
            return data;
          })
        ),
        catchError((error: unknown) => this.rethrowPasswordResetError(error))
      );
  }

  validatePasswordResetCaptcha(
    payload: ValidateCaptchaPayload
  ): Observable<ValidateCaptchaResponse> {
    return this.http
      .post<
        McResponse<ValidateCaptchaResponse>
      >(RESET_PASSWORD_VALIDATE_CAPTCHA_URL, payload)
      .pipe(
        map((response) =>
          this.mapPasswordResetResponse(
            response,
            (data) => {
              if (!data?.notificationType || !data?.token) {
                throw new PasswordResetError(
                  PASSWORD_RESET_ERROR_CODES.GENERIC
                );
              }
              return data;
            },
            PASSWORD_RESET_ERROR_CODES.CAPTCHA
          )
        ),
        catchError((error: unknown) => this.rethrowPasswordResetError(error))
      );
  }

  sendPasswordResetNotification(
    payload: SendNotificationPayload
  ): Observable<SendNotificationResponse> {
    return this.http
      .post<
        McResponse<SendNotificationResponse>
      >(RESET_PASSWORD_SEND_NOTIFICATION_URL, payload)
      .pipe(
        map((response) =>
          this.mapPasswordResetResponse(response, (data) => {
            if (!data?.token) {
              throw new PasswordResetError(PASSWORD_RESET_ERROR_CODES.GENERIC);
            }
            return data;
          })
        ),
        catchError((error: unknown) => this.rethrowPasswordResetError(error))
      );
  }

  confirmPasswordResetCode(
    payload: SendConfirmationPayload
  ): Observable<SendConfirmationResponse> {
    return this.http
      .post<
        McResponse<SendConfirmationResponse>
      >(RESET_PASSWORD_SEND_CONFIRMATION_URL, payload)
      .pipe(
        map((response) =>
          this.mapPasswordResetResponse(
            response,
            (data) => {
              if (!data?.passwordRules || !data?.token) {
                throw new PasswordResetError(
                  PASSWORD_RESET_ERROR_CODES.GENERIC
                );
              }
              return data;
            },
            PASSWORD_RESET_ERROR_CODES.RESET_CODE
          )
        ),
        catchError((error: unknown) => this.rethrowPasswordResetError(error))
      );
  }

  resetPassword(payload: ResetPasswordPayload): Observable<void> {
    return this.http
      .post<McResponse<unknown>>(RESET_PASSWORD_RESET_URL, payload)
      .pipe(
        map((response) => {
          this.mapPasswordResetResponse(response, () => undefined);
        }),
        catchError((error: unknown) => this.rethrowPasswordResetError(error))
      );
  }

  /**
   * Registers a TOTP authenticator for an account with no registered device.
   * Reached via the `otpToken` issued on the first-factor login/MFA response - a
   * blank/undefined token is rejected by the backend with `err_validation_blank`,
   * so callers must always pass a real route token (never call this with `undefined`).
   */
  registerOtp(token: string): Observable<OtpRegistrationData | undefined> {
    const payload: RegisterOtpRequest = { token };

    return this.http
      .post<McResponse<OtpRegistrationData>>(REGISTER_OTP_URL, payload)
      .pipe(
        map((response) => response.data?.[0] as OtpRegistrationData | undefined)
      );
  }

  /**
   * Shared by the Secure Messaging forgot-password (`isPasswordExpired: false`)
   * and expired-password (`isPasswordExpired: true`) journeys - same endpoint,
   * differentiated only by that flag.
   */
  requestSecureMessagingPasswordReset(
    emailAddress: string,
    isPasswordExpired: boolean
  ): Observable<void> {
    const payload: SecureMessagingResetPasswordPayload = {
      emailAddress,
      isPasswordExpired,
      ...this.resolveCustomerToken(),
    };

    return this.http
      .post<McResponse<unknown>>(SM_RESET_PASSWORD_URL, payload)
      .pipe(
        map((response) => {
          this.assertNoFailure(response);
          return undefined;
        })
      );
  }

  /**
   * Loads the API-provided password-complexity policy for the enforced
   * password-change journey.
   */
  getPasswordRules(
    request: GetPasswordRulesRequest
  ): Observable<PasswordRulesConfig>;
  /** Loads password rules for the Secure Messaging change-password link flow. */
  getPasswordRules(forgotPasswordToken: string): Observable<PasswordRules>;
  getPasswordRules(
    requestOrToken: GetPasswordRulesRequest | string
  ): Observable<PasswordRulesConfig | PasswordRules> {
    if (typeof requestOrToken === 'string') {
      const payload: GetPasswordRulesPayload = {
        forgotPasswordToken: requestOrToken,
      };

      return this.http
        .post<McResponse<PasswordRules>>(GET_PASSWORD_RULES_URL, payload)
        .pipe(
          map((response) => {
            this.assertNoFailure(response);
            const rules = response.data?.[0];
            if (!rules) {
              throw new Error('secure_messaging_password_request_failed');
            }
            return rules;
          })
        );
    }

    const payload: ApiRequest<GetPasswordRulesRequest> = {
      data: [requestOrToken],
    };

    return this.http
      .post<
        McResponse<PasswordRulesConfig>
      >(GET_PASSWORD_RULES_URL, payload, { headers: this.buildLoginHeaders() })
      .pipe(
        map((response) => this.mapPasswordRulesResponse(response)),
        catchError((error: unknown) =>
          throwError(() => this.buildChangePasswordError(error))
        )
      );
  }

  /** Submits the enforced password change using the server-issued changePasswordToken. */
  changePassword(
    request: ChangePasswordRequest
  ): Observable<ChangePasswordResponseData> {
    const payload: ApiRequest<ChangePasswordRequest> = { data: [request] };

    return this.http
      .post<
        McResponse<ChangePasswordResponseData>
      >(CHANGE_PASSWORD_URL, payload, { headers: this.buildLoginHeaders() })
      .pipe(
        map((response) => this.mapChangePasswordResponse(response)),
        catchError((error: unknown) =>
          throwError(() => this.buildChangePasswordError(error))
        )
      );
  }

  /**
   * Registers or replaces the user's SMS number for 2-step auth.
   * Requires the temporary MFA session via the `sid` header.
   */
  enrollSms(
    payload: EnrollSmsRequest,
    sessionId?: string | null
  ): Observable<EnrollSmsResponse> {
    const body: ApiRequest<EnrollSmsRequest> = {
      data: [payload],
    };

    return this.http
      .post<
        McResponse<EnrollSmsResponse>
      >(ENROLL_SMS_URL, body, { headers: this.buildLoginHeaders(sessionId) })
      .pipe(
        map((response) => this.mapEnrollSmsResponse(response)),
        catchError((error: unknown) =>
          throwError(() => {
            if (error instanceof LoginError) {
              return error;
            }

            const responseBody =
              ((error as HttpErrorResponse).error as LoginFailureBody | null) ??
              {};
            return this.buildLoginError(responseBody);
          })
        )
      );
  }

  setSecureMessagingPassword(
    payload: SetSecureMessagingPasswordPayload
  ): Observable<void> {
    return this.http
      .post<McResponse<unknown>>(SM_SET_PASSWORD_URL, payload)
      .pipe(map((response) => this.assertNoFailure(response)));
  }

  private mapPasswordRulesResponse(
    response: McResponse<PasswordRulesConfig>
  ): PasswordRulesConfig {
    if (response.fail?.length) {
      throw this.buildChangePasswordError(response);
    }

    const data = response.data?.[0] as PasswordRulesConfig | undefined;
    if (!data) {
      throw new ChangePasswordError('malformed_password_rules');
    }

    return data;
  }

  private mapChangePasswordResponse(
    response: McResponse<ChangePasswordResponseData>
  ): ChangePasswordResponseData {
    if (response.fail?.length) {
      throw this.buildChangePasswordError(response);
    }

    const data = response.data?.[0] as ChangePasswordResponseData | undefined;
    if (!data) {
      throw new ChangePasswordError('malformed_change_password_response');
    }

    return data;
  }

  private buildChangePasswordError(error: unknown): ChangePasswordError {
    if (error instanceof ChangePasswordError) {
      return error;
    }

    const httpBody = (error as HttpErrorResponse)?.error as
      | LoginFailureBody
      | null
      | undefined;
    const body = httpBody ?? (error as LoginFailureBody) ?? {};
    return new ChangePasswordError(body.fail?.[0]?.errors?.[0]?.code ?? '');
  }

  private assertNoFailure(
    response: McResponse<unknown> | null | undefined
  ): void {
    const hasErrorCode = response?.fail?.some((entry) =>
      entry.errors?.some((error) => Boolean(error.code))
    );

    if (hasErrorCode) {
      throw new Error('secure_messaging_password_request_failed');
    }
  }

  private buildLoginHeaders(sessionId?: string | null): HttpHeaders {
    const { xMcAppId } = this.configService.resolveAppConfig();
    const headers: Record<string, string> = {};

    if (xMcAppId) {
      headers['x-mc-app-id'] = xMcAppId;
    }

    if (sessionId) {
      headers['sid'] = sessionId;
    }

    return new HttpHeaders(headers);
  }

  private resolveCustomerToken(): { customerToken?: string } {
    const customerToken =
      this.configService.initialUrlParams.get('customerToken') ??
      this.configService.initialUrlParams.get('tkn') ??
      undefined;

    // Upstream redirects sometimes stringify a missing token as "undefined".
    if (!customerToken || customerToken === 'undefined') {
      return {};
    }

    return { customerToken };
  }

  private mapDiscoverAuthResponse(
    response: McResponse<DiscoverAuthenticationData>
  ): DiscoverAuthenticationResponse {
    if (response.fail?.length) {
      throw new Error('discover_failed');
    }

    const first = response.data?.[0] as DiscoverAuthenticationData | undefined;
    if (!first?.emailAddress) {
      throw new Error('discover_failed');
    }

    const authenticate = first.authenticate ?? [];
    const hasSaml = authenticate.some((type) => type.toUpperCase() === 'SAML');
    const hasOidc = authenticate.some((type) => type.toUpperCase() === 'OIDC');
    const authenticationTypes = this.mapAuthenticationTypes(
      authenticate,
      first.lastAuthType
    );

    /*
     * When region is present but authenticate is empty, return the response so
     * handleManualDiscoveryResult can evaluate a cross-grid redirect before
     * any auth-type validation. Example: a user on login-us.mimecast.com whose
     * account lives on the USB grid -- region.code "usb" triggers a redirect to
     * login-usb.mimecast.com instead of showing an auth error.
     *
     * hasOidc alone does not guarantee the UI can complete login: Microsoft
     * sign-in is also gated by ROLLOUT_MUL_ONEMC_LOGIN_VIA_MICROSOFT, which
     * this service has no visibility into. Callers (LoginFormComponent) must
     * treat authenticationTypes: [] + hasOidc: true + flag-off as "no usable
     * login method" and show a discovery error, not a blank step.
     */
    if (
      authenticationTypes.length === 0 &&
      !hasSaml &&
      !hasOidc &&
      !first.region
    ) {
      throw new Error('no_valid_auth_types');
    }

    const selectedAuthenticationType =
      authenticationTypes.find((type) => type.selected) ??
      authenticationTypes[0];

    return {
      emailAddress: first.emailAddress,
      authenticationTypes,
      selectedAuthenticationType,
      hasSaml,
      hasOidc,
      // Whenever Basic-External is present hide the auth type dropdown entirely,
      // even when other types(e.g. Basic-AD) are offered alongside it.
      isExternal: authenticationTypes.some(
        (type) => type.key === 'Basic-External'
      ),
      emailToken: first.emailToken,
      region: first.region,
    };
  }

  private mapAuthenticationTypes(
    authenticate: string[],
    lastAuthType?: string
  ): AuthenticationType[] {
    const normalizedLastAuthType = lastAuthType
      ? this.normalizeAuthTypeKey(lastAuthType)
      : undefined;

    const authenticationTypes = [...authenticate]
      .reverse()
      .map((type) => this.normalizeAuthTypeKey(type))
      .filter((key): key is ValidAuthTypeKey => key !== undefined)
      .map((key) => ({
        key,
        selected: key === normalizedLastAuthType,
      }));

    if (
      authenticationTypes.length > 0 &&
      !authenticationTypes.some((type) => type.selected)
    ) {
      authenticationTypes[0] = { ...authenticationTypes[0], selected: true };
    }

    return authenticationTypes;
  }

  private normalizeAuthTypeKey(value: string): ValidAuthTypeKey | undefined {
    return AUTH_TYPE_KEY_BY_UPPERCASE_VALUE[value.toUpperCase()];
  }

  private mapLoginResponse(
    response: McResponse<LoginResponseData>
  ): LoginResponseData {
    if (response.fail?.length) {
      throw this.buildLoginError(response);
    }

    const data = response.data?.[0] as LoginResponseData | undefined;
    this.setLastUserToken(data?.lastUserToken);
    this.loginSessionService.setSession({
      sessionId: data?.sessionId,
      remoteAccountCode: data?.remoteAccountCode,
    });
    return data ?? {};
  }

  private mapVerificationCodeResponse(
    response: McResponse<SubmitVerificationCodeResponse>
  ): SubmitVerificationCodeResponse {
    if (response.fail?.length) {
      throw this.buildMfaError(response);
    }

    const data = response.data?.[0] as
      | SubmitVerificationCodeResponse
      | undefined;
    this.setLastUserToken(data?.lastUserToken);
    this.loginSessionService.setSession({
      sessionId: data?.sessionId,
      remoteAccountCode: data?.remoteAccountCode,
    });
    return data ?? {};
  }

  private mapEnrollSmsResponse(
    response: McResponse<EnrollSmsResponse>
  ): EnrollSmsResponse {
    if (response.fail?.length) {
      throw this.buildLoginError(response);
    }

    const data = response.data?.[0] as EnrollSmsResponse | undefined;
    this.setLastUserToken(data?.lastUserToken);
    this.loginSessionService.setSession({
      sessionId: data?.sessionId,
      remoteAccountCode: data?.remoteAccountCode,
    });
    return data ?? {};
  }

  private mapFederationResponse(
    response: McResponse<LoginResponseData>
  ): LoginResponseData {
    if (response.fail?.length) {
      throw this.buildLoginError(response);
    }

    const data = response.data?.[0] as LoginResponseData | undefined;
    this.loginSessionService.setSession({
      sessionId: data?.sessionId,
      remoteAccountCode: data?.remoteAccountCode,
    });
    return data ?? {};
  }

  private buildLoginError(body: LoginFailureBody): LoginError {
    const key = body.fail?.[0]?.key ?? {};

    this.setLastUserToken(key['lastUserToken'] as string | undefined);

    return new LoginError(this.getFailCode(body), {
      changePasswordToken: body.data?.[0]?.changePasswordToken,
      otpToken: key['otpToken'] as string | undefined,
      verificationCodeDeliveryMethod: key['verificationCodeDeliveryMethod'] as
        | number
        | undefined,
      verificationCodeDeliveryAddress: key[
        'verificationCodeDeliveryAddress'
      ] as string | undefined,
      sessionId: key['sessionId'] as string | undefined,
    });
  }

  private buildMfaError(body: LoginFailureBody): MfaError {
    const key = body.fail?.[0]?.key ?? {};
    // Legacy errorHandler sets `lu` from the fail key for submit-verification
    // failures as well as login failures.
    this.setLastUserToken(key['lastUserToken'] as string | undefined);
    return new MfaError(this.getFailCode(body));
  }

  /**
   * Resolves HttpErrorResponse.error whether object, JSON string, or a body
   * already shaped as LoginFailureBody. Never treat fail[0].key.code
   * as the API error code - callers must use getFailCode / errors[0].code.
   */
  private resolveFailureBody(error: unknown): LoginFailureBody {
    if (error && typeof error === 'object' && 'error' in error) {
      const httpBody = (error as HttpErrorResponse).error;
      if (typeof httpBody === 'string') {
        try {
          return JSON.parse(httpBody) as LoginFailureBody;
        } catch {
          return {};
        }
      }
      if (httpBody && typeof httpBody === 'object') {
        return httpBody as LoginFailureBody;
      }
    }

    if (error && typeof error === 'object' && 'fail' in error) {
      return error as LoginFailureBody;
    }

    return {};
  }

  private mapPasswordResetResponse<T>(
    response: McResponse<T>,
    mapData: (data: T | undefined) => T,
    failCode: string = PASSWORD_RESET_ERROR_CODES.GENERIC
  ): T {
    if (response.fail?.length) {
      const code = this.getFailCode(response);
      throw new PasswordResetError(
        code === PASSWORD_RESET_ERROR_CODES.RESET_CODE_EXPIRED
          ? PASSWORD_RESET_ERROR_CODES.RESET_CODE_EXPIRED
          : failCode
      );
    }

    return mapData(response.data?.[0] as T | undefined);
  }

  private rethrowPasswordResetError(error: unknown): Observable<never> {
    return throwError(() => {
      if (error instanceof PasswordResetError) {
        return error;
      }

      const body =
        ((error as HttpErrorResponse).error as LoginFailureBody | null) ?? {};
      const code = this.getFailCode(body);
      return new PasswordResetError(
        code === PASSWORD_RESET_ERROR_CODES.RESET_CODE_EXPIRED
          ? PASSWORD_RESET_ERROR_CODES.RESET_CODE_EXPIRED
          : PASSWORD_RESET_ERROR_CODES.GENERIC
      );
    });
  }

  private getFailCode(body: LoginFailureBody): string {
    return body.fail?.[0]?.errors?.[0]?.code ?? '';
  }

  /**
   * Writes the `lu` cookie (client-side only, no server session change) for login
   * success, login errors, submit-verification success, and submit-verification
   * errors - matching legacy `setLastUserToken` in `errorHandler` / success handlers.
   * Always called synchronously before the `map`/`catchError` pipeline emits so the
   * cookie is set before any redirect or error-code navigation.
   *
   * Skipped for secure (entry `gta=secure`) logins. A missing/empty token is a no-op -
   * it never clears or overwrites an existing `lu` cookie. SAML flows never reach this
   * method, so no explicit SAML check is needed here.
   */
  private setLastUserToken(lastUserToken?: string): void {
    if (!lastUserToken) {
      return;
    }

    const isSecure =
      this.configService.initialUrlParams.get('gta') === 'secure';
    if (isSecure) {
      return;
    }

    this.cookieService.set(LAST_USER_TOKEN_COOKIE, lastUserToken, {
      path: '/',
      secure: true,
    });
  }
}
